import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatHistorySession } from './ChatHistorySession';

export type ChatHistoryStatus = 'loading' | 'loaded' | 'error';

export interface ChatHistoryQuery {
  /**
   * When set, restricts the list to sessions for this routine. When
   * null, lists all sessions across all routines (the non-routine /
   * global context).
   */
  routineID: string | null;
}

export interface ChatHistoryOperations {
  /**
   * Loads sessions matching the query. Implementations call feathers
   * `agent-sessions.find({ query: { routineID?, $sort: { updatedAt: -1 }}})`.
   */
  list: (query: ChatHistoryQuery) => Promise<ChatHistorySession[]>;
  /**
   * Updates the title (`patch`). Returns the updated row so the hook
   * can reflect the rename without a refetch.
   */
  rename: (sessionID: string, newTitle: string) => Promise<ChatHistorySession>;
  /**
   * Soft-deletes the session (`remove`). The hook treats 404 as
   * idempotent success — the row is gone from the local list
   * regardless, matching `useResolveConfirmation`'s 404 handling.
   */
  remove: (sessionID: string) => Promise<void>;
}

export interface UseChatHistoryOptions extends ChatHistoryQuery {
  /** Injected feathers operations; the production set is wired in
   * by the chat mount. */
  operations: ChatHistoryOperations;
  /** Fires on any failed operation. */
  onError?: (error: Error) => void;
}

export interface UseChatHistoryResult {
  status: ChatHistoryStatus;
  sessions: ChatHistorySession[];
  error: Error | null;
  /** Refetches the list — exposed so callers can refresh after navigation. */
  reload: () => void;
  /** Patches the title locally + server-side. */
  renameSession: (sessionID: string, newTitle: string) => Promise<void>;
  /** Removes the session locally + server-side. */
  deleteSession: (sessionID: string) => Promise<void>;
}

/**
 * Drives the chat-history list. Re-fetches whenever `routineID`
 * changes (a routine-page → non-routine-page navigation flips the
 * filter). Rename / delete mutate the local list optimistically so
 * the UI stays responsive while the patch / remove is in flight;
 * server failures roll the optimistic change back and surface via
 * `onError`.
 *
 * All operations are injected (`operations.list / .rename / .remove`)
 * so tests can stub them and the production mount plugs in the real
 * feathers client. Callback identities are absorbed via refs so
 * callers can pass inline closures without re-fetch loops.
 */

/**
 * 404-shaped error — feathers errors carry `{ code: 404 }`; bare
 * fetch / app code might carry `status: 404` or a 404 token in the
 * message. The hook treats all three as idempotent "already gone."
 */
function isNotFoundError(error: Error): boolean {
  const candidate = error as Error & { code?: unknown; status?: unknown };

  if (candidate.code === 404 || candidate.status === 404) return true;

  return /\b404\b/.test(error.message);
}

export function useChatHistory(
  options: UseChatHistoryOptions,
): UseChatHistoryResult {
  const { routineID, operations, onError } = options;

  const operationsRef = useRef(operations);
  const onErrorRef = useRef(onError);

  operationsRef.current = operations;
  onErrorRef.current = onError;

  const [status, setStatus] = useState<ChatHistoryStatus>('loading');
  const [sessions, setSessions] = useState<ChatHistorySession[]>([]);
  const [error, setError] = useState<Error | null>(null);

  // Mirror sessions so optimistic-update callbacks can read the
  // current list synchronously when they fire (the React updater
  // for setSessions doesn't run until the next render, which is
  // too late if `await someOp()` rejects before that commit).
  const sessionsRef = useRef(sessions);

  sessionsRef.current = sessions;

  const reloadTokenRef = useRef(0);
  const [reloadCount, setReloadCount] = useState(0);

  const reload = useCallback(() => {
    setReloadCount((n) => n + 1);
  }, []);

  useEffect(() => {
    reloadTokenRef.current += 1;

    const myToken = reloadTokenRef.current;

    setStatus('loading');
    setError(null);

    operationsRef.current
      .list({ routineID })
      .then((rows) => {
        if (myToken !== reloadTokenRef.current) return;

        setSessions(rows);
        setStatus('loaded');
      })
      .catch((err: unknown) => {
        if (myToken !== reloadTokenRef.current) return;

        const wrapped = err instanceof Error ? err : new Error(String(err));

        setError(wrapped);
        setStatus('error');
        onErrorRef.current?.(wrapped);
      });
  }, [routineID, reloadCount]);

  const renameSession = useCallback(
    async (sessionID: string, newTitle: string) => {
      // Per-item revert: snapshot the row's title synchronously
      // from sessionsRef (the React updater for the optimistic
      // setSessions below doesn't run until the next commit, which
      // can be after the awaited op rejects). On failure, only the
      // affected row is touched — concurrent mutations on other
      // rows survive intact.
      const existing = sessionsRef.current.find((s) => s.id === sessionID);

      if (!existing) return;

      const originalTitle = existing.title;

      setSessions((current) =>
        current.map((s) =>
          s.id === sessionID ? { ...s, title: newTitle } : s,
        ),
      );

      try {
        const updated = await operationsRef.current.rename(sessionID, newTitle);

        setSessions((current) =>
          current.map((s) => (s.id === sessionID ? updated : s)),
        );
      } catch (err) {
        setSessions((current) =>
          current.map((s) =>
            s.id === sessionID ? { ...s, title: originalTitle } : s,
          ),
        );

        const wrapped = err instanceof Error ? err : new Error(String(err));

        onErrorRef.current?.(wrapped);
      }
    },
    [],
  );

  const deleteSession = useCallback(async (sessionID: string) => {
    // Per-item revert: snapshot the row + its index from
    // sessionsRef, optimistically remove, and on failure re-insert
    // just that row near its original position. Other rows in
    // flight remain untouched.
    const existingIndex = sessionsRef.current.findIndex(
      (s) => s.id === sessionID,
    );

    if (existingIndex < 0) return;

    const existing = sessionsRef.current[existingIndex];

    setSessions((current) => current.filter((s) => s.id !== sessionID));

    try {
      await operationsRef.current.remove(sessionID);
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));

      // 404 is idempotent success — the row is gone server-side, so
      // leave the local list with it removed and don't surface as an
      // error.
      if (isNotFoundError(wrapped)) {
        return;
      }

      setSessions((current) => {
        if (current.some((s) => s.id === sessionID)) return current;

        const next = current.slice();
        const insertAt = Math.min(existingIndex, next.length);

        next.splice(insertAt, 0, existing);

        return next;
      });

      onErrorRef.current?.(wrapped);
    }
  }, []);

  return {
    status,
    sessions,
    error,
    reload,
    renameSession,
    deleteSession,
  };
}
